'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Card } from '@/shared/ui/shadcn/card';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Trash2, AlertTriangle, XCircle } from 'lucide-react';
import { userService } from '@/features/auth/api/user.service';
import { useAuthStore } from '@/features/auth/model/auth.store';
import { useUser } from '@/features/auth/model/auth.selectors';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { useProfile } from '@/shared/api/hooks/use-user';

const DELETE_CONFIRMATION_PHRASE = 'DELETE ALL DATA';

const isConfirmationValid = (input: string): boolean =>
  input.trim().toUpperCase() === DELETE_CONFIRMATION_PHRASE;

export function AccountDeletionSection() {
  const t = useTranslations('accountSettings.deleteAccount');
  const locale = useLocale();

  const user = useUser();
  const refreshUser = useAuthStore((state) => state.refreshUser);

  // Server-driven free-tier retention line ("come back tomorrow for N more free
  // designs"). Non-null only while the free tier is active AND the daily refill
  // is still available — shown as a soft reason to stay before deleting. Copy is
  // server-localized; render verbatim (never compose it client-side).
  const freeTier = useProfile().data?.free_tier;
  const retentionLabel =
    freeTier?.is_active && freeTier.refill_label ? freeTier.refill_label : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const deletionStatus = user?.deletion_status;
  const isScheduled = deletionStatus?.is_scheduled_for_deletion ?? false;
  const scheduledAt = deletionStatus?.scheduled_deletion_at ?? null;
  const daysRemaining = deletionStatus?.days_remaining ?? null;
  const canCancel = deletionStatus?.can_cancel_deletion ?? false;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      await refreshUser();
      setError(null);
    } catch (err) {
      console.error('Failed to load deletion status:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, refreshUser]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRequestDeletion = async () => {
    setIsSubmitting(true);
    setDialogError(null);

    try {
      await userService.requestAccountDeletion(reason || undefined);
      await refreshUser();

      setShowConfirmDialog(false);
      setConfirmation('');
      setReason('');
      setDialogError(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('errors.requestFailed');
      setDialogError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    setError(null);

    try {
      await userService.cancelAccountDeletion();
      await refreshUser();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('errors.cancelFailed');
      setError(message);
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-2" />
          <div className="h-4 bg-muted rounded w-2/3" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-destructive/50">
      <div className="p-6">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isScheduled ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('scheduled.message', {
                  days: daysRemaining ?? 0,
                  date: scheduledAt
                    ? new Date(scheduledAt).toLocaleDateString(locale)
                    : '',
                })}
              </AlertDescription>
            </Alert>

            {canCancel && (
              <Button
                variant="outline"
                onClick={handleCancelDeletion}
                disabled={isCancelling}
                className="w-full sm:w-auto"
              >
                <XCircle className="h-4 w-4 mr-2" />
                {isCancelling ? t('cancelling') : t('cancelButton')}
              </Button>
            )}
          </div>
        ) : (
          <Button
            variant="destructive"
            onClick={() => setShowConfirmDialog(true)}
            className="w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('requestButton')}
          </Button>
        )}
      </div>

      <Dialog
        open={showConfirmDialog}
        onOpenChange={(open) => {
          setShowConfirmDialog(open);
          if (!open) {
            setDialogError(null);
            setConfirmation('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('dialog.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-form-help text-foreground">{t('dialog.warning')}</p>

            {/* Free-tier retention nudge — server-driven "come back tomorrow"
                line, shown only while the daily free refill is still available.
                Soft reason to stay; copy is rendered verbatim. */}
            {retentionLabel && (
              <NoticeBanner variant="info" message={retentionLabel} />
            )}

            {dialogError && (
              <Alert variant="destructive">
                <AlertDescription>{dialogError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="confirmation">
                {t('dialog.confirmLabel', {
                  phrase: DELETE_CONFIRMATION_PHRASE,
                })}
              </Label>
              <Input
                id="confirmation"
                type="text"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={DELETE_CONFIRMATION_PHRASE}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">{t('dialog.reasonLabel')}</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('dialog.reasonPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isSubmitting}
            >
              {t('dialog.cancelButton')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRequestDeletion}
              disabled={isSubmitting || !isConfirmationValid(confirmation)}
            >
              {isSubmitting
                ? t('dialog.submitting')
                : t('dialog.confirmButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
